<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'

const selectedDates = defineModel<Array<any>>({
  default: []
})

const isOpen = ref(false)
const triggerRef = ref<HTMLElement | null>(null)
const calendarRef = ref<HTMLElement | null>(null)

const numbers = Array.from({ length: 31 }, (_, i) => i + 1)

const getOrdinal = (n: number): string => {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return n + (s[(v - 20) % 10] || s[v] || s[0])
}

const isLastDaySelected = computed(() => selectedDates.value.includes(-1))

const formattedSelectedDates = computed(() => {
  const { value: dates } = selectedDates
  if (dates.length === 0) return 'None'

  const filteredDates = dates.filter((num) => num !== -1).sort((a, b) => a - b)
  const formattedDates = filteredDates.slice(0, 6).map(getOrdinal).join(', ')

  const lastDayString = 
	isLastDaySelected.value
  ? dates.length > 1
      ? ', Last Day'
      : 'Last Day'
  : ''

  const selectedDatesString = [formattedDates, lastDayString].filter(Boolean).join('')

  const extraCount = dates.length - filteredDates.slice(0, 6).length - (isLastDaySelected.value ? 1 : 0)
  return extraCount > 0 ? `${selectedDatesString} +${extraCount} more` : selectedDatesString
})

const isSelected = (num: number) => selectedDates.value.includes(num)

const handleClick = (event: Event, num: number) => {
  event.preventDefault()
  if (isSelected(num)) {
    selectedDates.value = selectedDates.value.filter((n) => n !== num)
  } else {
    selectedDates.value = [...selectedDates.value, num]
  }
}

const chunks = computed(() => {
  const result = []
  for (let i = 0; i < numbers.length; i += 7) {
    result.push(numbers.slice(i, i + 7))
  }
  return result
})

const handleClickOutside = (event: MouseEvent) => {
  if (
    isOpen.value &&
    triggerRef.value &&
    calendarRef.value &&
    !triggerRef.value.contains(event.target as Node) &&
    !calendarRef.value.contains(event.target as Node)
  ) {
    isOpen.value = false
  }
}

const toggleLastDay = (event: Event) => {
  event.preventDefault()
  if (selectedDates.value.includes(-1)) {
    selectedDates.value = selectedDates.value.filter((n) => n !== -1)
  } else {
    selectedDates.value = [...selectedDates.value, -1]
  }
}

const handleReset = (event: Event) => {
  event.preventDefault()
  selectedDates.value = []
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <div class="calendar-container">
    <div class="dates-selection-label pb-2">Selected dates</div>
    <div ref="triggerRef" class="dates-input-trigger" @click="isOpen = !isOpen">
      <span v-if="formattedSelectedDates?.length > 0">
        {{ formattedSelectedDates }}
      </span>
      <span v-else> </span>
    </div>
    <div v-if="isOpen" ref="calendarRef" class="calendar">
      <div class="grid">
        <div v-for="row in chunks" :key="row[0]" class="row">
          <button
            v-for="num in row"
            :key="num"
            @click="(event) => handleClick(event, num)"
            :class="{ selected: isSelected(num) }"
            class="cell"
            type="button"
          >
            {{ num }}
          </button>
        </div>
        <div class="row">
          <button
            @click="toggleLastDay"
            class="calendar-action-button calendar-last-day-button"
            :class="{ selected: isLastDaySelected }"
            type="button"
          >
            Last Day
          </button>
          <button 
            @click="handleReset"
            class="calendar-action-button calendar-reset-button"
            type="button"
          >
            Reset
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
$rounded-border-radius: 4px;

.calendar-container {
  position: relative;
}

.dates-input-trigger {
  width: 100%;
  border: 1px solid #cbd5e1;
  background: #f0f4f7;
  padding: 0.75rem;
  cursor: pointer;
  border-radius: 4px;
  user-select: none;
}

.calendar {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 1rem;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
  z-index: 10;
}

.grid {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  user-select: none;
}

.row {
  display: flex;
  gap: 0.5rem;
}

.cell {
  width: 2.5rem;
  height: 2.5rem;
  border: 1px solid #ddd;
  border-radius: $rounded-border-radius;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: none;
}

.cell:hover {
  opacity: 0.6;
}

.selected {
  background-color: #3b82f6;
  border-radius: $rounded-border-radius;
  color: white;
}

.dates-selection-label {
  color: #717171 !important;
}

.calendar-action-button {
  width: 100px;
  height: 40px;
  position: absolute;
  border-radius: $rounded-border-radius;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.calendar-last-day-button {
  bottom: 24px;
  right: 100px;
}

.calendar-reset-button {
  width: 76px;
  bottom: 24px;
  right: 16px;
  background-color: #f0f4f7;
}
</style>
